import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';
import { Plus, Trash2, Package, TrendingDown, Filter, X, AlertTriangle, ArrowRight, Factory } from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

const UNITS = ['pcs','sqft','rft','kg','ltr','mtr','sheet','bag','box','ton'];
const MATERIAL_SUGGESTIONS = ['Solid Wood','MDF Sheet','Plywood','Hardware','Glass','Fabric','Foam','Paint','Polish','Edge Tape','Screw Set','Laminate','Veneer','Granite','Marble'];

const emptyConsForm = { project_id:'', project_item_id:'', department_id:'', material_type:'consumption', notes:'', entry_date: new Date().toISOString().slice(0,10) };
const emptyMaterialRow = { material_name:'', consumed_qty:'', unit:'kg', rate_per_unit:'' };
const emptyProdForm = { project_id:'', project_item_id:'', department_id:'', item_name:'', produced_qty:'', unit:'pcs', rate_per_unit:'', notes:'', entry_date: new Date().toISOString().slice(0,10) };

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400";
const selectCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400";
const FormField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default function MaterialPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('materials');
  const { formatCurrency } = useSettings();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('ledger'); // ledger | summary | entries
  const [showConsForm, setShowConsForm] = useState(false);
  const [showProdForm, setShowProdForm] = useState(false);
  const [consForm, setConsForm] = useState(emptyConsForm);
  const [materialRows, setMaterialRows] = useState([{ ...emptyMaterialRow }]);
  const [prodForm, setProdForm] = useState(emptyProdForm);
  const [consItems, setConsItems] = useState([]);
  const [prodItems, setProdItems] = useState([]);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.filter(p => p.status !== 'deleted'))).catch(()=>{});
    api.get('/departments', { params: { active_only: '1' } }).then(r => setDepartments(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (consForm.project_id) {
      api.get(`/projects/${consForm.project_id}`).then(r => setConsItems(r.data.items || [])).catch(()=>{});
    }
  }, [consForm.project_id]);

  useEffect(() => {
    if (prodForm.project_id) {
      api.get(`/projects/${prodForm.project_id}`).then(r => setProdItems(r.data.items || [])).catch(()=>{});
    }
  }, [prodForm.project_id]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProject) params.project_id = selectedProject;
      const res = await api.get('/reports/materials', { params });
      setReportData(res.data);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleConsSubmit = async () => {
    if (!consForm.project_id) return toast.error('Please select a project');
    const validRows = materialRows.filter(r => r.material_name.trim() && r.consumed_qty);
    if (!validRows.length) return toast.error("Add at least one material's details");
    try {
      for (const row of validRows) {
        await api.post('/materials', {
          ...consForm,
          material_name: row.material_name,
          consumed_qty: row.consumed_qty,
          unit: row.unit,
          rate_per_unit: row.rate_per_unit || 0,
        });
      }
      toast.success(`${validRows.length} material entr${validRows.length > 1 ? 'ies' : 'y'} saved ✅`);
      setShowConsForm(false);
      setConsForm(emptyConsForm);
      setMaterialRows([{ ...emptyMaterialRow }]);
      load();
    } catch(err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleProdSubmit = async () => {
    if (!prodForm.project_id || !prodForm.item_name || !prodForm.produced_qty) return toast.error('Project, Item, and Qty are required');
    try {
      await api.post('/production-output', prodForm);
      toast.success('Production entry saved ✅');
      setShowProdForm(false);
      setProdForm(emptyProdForm);
      load();
    } catch(err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const delCons = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/materials/${id}`); toast.success('Deleted'); load();
  };

  const delProd = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/production-output/${id}`); toast.success('Deleted'); load();
  };

  const consTotal = reportData?.totalConsumptionCost || 0;
  const prodTotal = reportData?.totalProductionCost || 0;
  const wastageVal = reportData?.wastageValue || 0;
  const wastePct = consTotal > 0 ? ((wastageVal / consTotal) * 100).toFixed(1) : 0;
  const detail = reportData?.detail || [];
  const production = reportData?.production || [];

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🪵 Material Ledger</h1>
          <p className="text-gray-500 text-sm mt-0.5">Consumption vs Production — Consumption vs Production Ledger</p>
        </div>
        <div className="flex gap-2">
          {canWrite && <button onClick={() => setShowConsForm(true)} className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-xl text-sm font-semibold">
            <Plus className="w-4 h-4" /> Consumption
          </button>}
          {canWrite && <button onClick={() => setShowProdForm(true)} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-sm font-semibold">
            <Factory className="w-4 h-4" /> Production
          </button>}
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4 flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">Project Filter</label>
          <ProjectSelect projects={projects} value={selectedProject} onChange={v => setSelectedProject(v)}
              placeholder="All Projects" className={selectCls} />
        </div>
        <button onClick={load} className="bg-amber-500 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2">
          <Filter className="w-4 h-4" /> Apply
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-xl"><Package className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-xl font-bold text-red-700">{formatCurrency(consTotal)}</p>
              <p className="text-xs text-red-500">Total Consumption (Input)</p>
              <p className="text-xs text-gray-400">{detail.length} entries</p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-xl"><Factory className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xl font-bold text-green-700">{formatCurrency(prodTotal)}</p>
              <p className="text-xs text-green-500">Total Production (Output)</p>
              <p className="text-xs text-gray-400">{production.length} entries</p>
            </div>
          </div>
        </div>
        <div className={`border rounded-2xl p-4 ${parseFloat(wastePct) > 20 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${parseFloat(wastePct) > 20 ? 'bg-orange-100' : 'bg-blue-100'}`}>
              <AlertTriangle className={`w-5 h-5 ${parseFloat(wastePct) > 20 ? 'text-orange-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <p className={`text-xl font-bold ${parseFloat(wastePct) > 20 ? 'text-orange-700' : 'text-blue-700'}`}>{formatCurrency(wastageVal)}</p>
              <p className={`text-xs ${parseFloat(wastePct) > 20 ? 'text-orange-500' : 'text-blue-500'}`}>Wastage ({wastePct}%)</p>
              <p className="text-xs text-gray-400">Input - Output</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[['ledger','📊 Ledger View (Excel jaisa)'],['entries','📋 Consumption'],['production','🏭 Production']].map(([v,l]) => (
          <button key={v} onClick={() => setActiveTab(v)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab===v ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* LEDGER VIEW — Side-by-side Ledger View */}
      {activeTab === 'ledger' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-600">📋 Consumption (Input) vs 🏭 Production (Output)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {/* CONSUMPTION SIDE */}
                  <td colSpan={5} className="bg-red-50 text-red-700 font-bold text-center px-4 py-2 border-b-2 border-red-300 border-r-4 border-r-gray-300">
                    CONSUMPTION (Materials Received)
                  </td>
                  {/* PRODUCTION SIDE */}
                  <td colSpan={5} className="bg-green-50 text-green-700 font-bold text-center px-4 py-2 border-b-2 border-green-300">
                    PRODUCTION (Output Produced)
                  </td>
                </tr>
                <tr className="text-xs text-gray-500 font-semibold">
                  <th className="text-left px-4 py-2 bg-red-50">Item Name</th>
                  <th className="text-center px-3 py-2 bg-red-50">Qty</th>
                  <th className="text-center px-3 py-2 bg-red-50">Rate</th>
                  <th className="text-right px-3 py-2 bg-red-50">Amount</th>
                  <th className="text-left px-3 py-2 bg-red-50 border-r-4 border-r-gray-300">Dept</th>
                  <th className="text-left px-4 py-2 bg-green-50">Item Name</th>
                  <th className="text-center px-3 py-2 bg-green-50">Qty</th>
                  <th className="text-center px-3 py-2 bg-green-50">Rate</th>
                  <th className="text-right px-3 py-2 bg-green-50">Amount</th>
                  <th className="text-left px-3 py-2 bg-green-50">Dept</th>
                </tr>
              </thead>
              <tbody>
                {Math.max(detail.length, production.length) === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-gray-400">
                      No entries yet — add Consumption or Production from above
                    </td>
                  </tr>
                ) : (
                  Array.from({ length: Math.max(detail.length, production.length) }).map((_, i) => {
                    const c = detail[i];
                    const p = production[i];
                    return (
                      <tr key={i} className={`border-t border-gray-50 ${i%2===0 ? '' : 'bg-gray-50/30'}`}>
                        {/* Consumption */}
                        <td className="px-4 py-2 bg-red-50/30">
                          {c ? <div>
                            <p className="font-medium text-gray-800">{c.material_name}</p>
                            {c.project_name && <p className="text-xs text-gray-400">{c.project_name}</p>}
                          </div> : <span className="text-gray-200">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center bg-red-50/30 text-red-700 font-medium">{c ? `${parseFloat(c.consumed_qty).toFixed(2)} ${c.unit}` : ''}</td>
                        <td className="px-3 py-2 text-center bg-red-50/30 text-gray-500">{c ? formatCurrency(c.rate_per_unit) : ''}</td>
                        <td className="px-3 py-2 text-right bg-red-50/30 font-semibold text-red-700">{c ? formatCurrency(c.total_amount) : ''}</td>
                        <td className="px-3 py-2 bg-red-50/30 border-r-4 border-r-gray-200 text-xs text-gray-500">
                          {c && <div className="flex items-center gap-1">
                            <span>{c.dept_name || '—'}</span>
                            <button onClick={() => delCons(c.id)} className="ml-1 text-red-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                          </div>}
                        </td>
                        {/* Production */}
                        <td className="px-4 py-2 bg-green-50/30">
                          {p ? <div>
                            <p className="font-medium text-gray-800">{p.item_name}</p>
                            {p.project_name && <p className="text-xs text-gray-400">{p.project_name}</p>}
                          </div> : <span className="text-gray-200">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center bg-green-50/30 text-green-700 font-medium">{p ? `${parseFloat(p.produced_qty).toFixed(2)} ${p.unit}` : ''}</td>
                        <td className="px-3 py-2 text-center bg-green-50/30 text-gray-500">{p ? formatCurrency(p.rate_per_unit) : ''}</td>
                        <td className="px-3 py-2 text-right bg-green-50/30 font-semibold text-green-700">{p ? formatCurrency(p.total_amount) : ''}</td>
                        <td className="px-3 py-2 bg-green-50/30 text-xs text-gray-500">
                          {p && <div className="flex items-center gap-1">
                            <span>{p.dept_name || '—'}</span>
                            <button onClick={() => delProd(p.id)} className="ml-1 text-red-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                          </div>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {(detail.length > 0 || production.length > 0) && (
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold text-sm">
                    <td className="px-4 py-2 text-gray-600" colSpan={3}>TOTAL</td>
                    <td className="px-3 py-2 text-right text-red-700">{formatCurrency(consTotal)}</td>
                    <td className="px-3 py-2 border-r-4 border-r-gray-200"></td>
                    <td className="px-4 py-2 text-gray-600" colSpan={3}>TOTAL</td>
                    <td className="px-3 py-2 text-right text-green-700">{formatCurrency(prodTotal)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-orange-50 font-bold text-sm">
                    <td colSpan={10} className="px-4 py-2 text-center text-orange-700">
                      ⚠️ Wastage = {formatCurrency(consTotal)} - {formatCurrency(prodTotal)} = <span className="text-lg">{formatCurrency(wastageVal)}</span>
                      <span className="ml-2 text-sm">({wastePct}%)</span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Consumption entries tab */}
      {activeTab === 'entries' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {detail.length === 0 ? (
            <div className="p-12 text-center text-gray-400"><Package className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No consumption entries</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="bg-red-50 text-xs text-gray-500">
                <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Material</th>
                <th className="text-left px-4 py-3">Project/Item</th><th className="text-left px-4 py-3">Dept</th>
                <th className="text-center px-4 py-3">Type</th><th className="text-center px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Rate</th><th className="text-right px-4 py-3">Amount</th>
                <th className="px-3 py-3"></th>
              </tr></thead>
              <tbody>
                {detail.map(r => (
                  <tr key={r.id} className="border-t border-gray-50 hover:bg-red-50/30">
                    <td className="px-4 py-2.5 text-gray-500">{r.entry_date?.slice(0,10)}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{r.material_name}</td>
                    <td className="px-4 py-2.5"><p className="text-gray-700">{r.project_name}</p>{r.item_name && <p className="text-xs text-gray-400">{r.proto_code} {r.item_name}</p>}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{r.dept_name || '—'}</td>
                    <td className="px-4 py-2.5 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.material_type==='wastage' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{r.material_type}</span></td>
                    <td className="px-4 py-2.5 text-center">{parseFloat(r.consumed_qty).toFixed(2)} {r.unit}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(r.rate_per_unit)}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-red-700">{formatCurrency(r.total_amount)}</td>
                    <td className="px-3 py-2.5"><button onClick={() => delCons(r.id)} className="text-red-300 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Production entries tab */}
      {activeTab === 'production' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {production.length === 0 ? (
            <div className="p-12 text-center text-gray-400"><Factory className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No production entries</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="bg-green-50 text-xs text-gray-500">
                <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Item Name</th>
                <th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Dept</th>
                <th className="text-center px-4 py-3">Qty</th><th className="text-right px-4 py-3">Rate</th>
                <th className="text-right px-4 py-3">Amount</th><th className="px-3 py-3"></th>
              </tr></thead>
              <tbody>
                {production.map(r => (
                  <tr key={r.id} className="border-t border-gray-50 hover:bg-green-50/30">
                    <td className="px-4 py-2.5 text-gray-500">{r.entry_date?.slice(0,10)}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{r.item_name}</td>
                    <td className="px-4 py-2.5"><p className="text-gray-700">{r.project_name}</p>{r.project_item_name && <p className="text-xs text-gray-400">{r.proto_code} {r.project_item_name}</p>}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{r.dept_name || '—'}</td>
                    <td className="px-4 py-2.5 text-center font-medium text-green-700">{parseFloat(r.produced_qty).toFixed(2)} {r.unit}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(r.rate_per_unit)}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-green-700">{formatCurrency(r.total_amount)}</td>
                    <td className="px-3 py-2.5"><button onClick={() => delProd(r.id)} className="text-red-300 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Consumption Form Modal */}
      {showConsForm && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b bg-red-50">
              <h2 className="font-bold text-lg text-red-700">📦 Add Consumption</h2>
              <button onClick={() => { setShowConsForm(false); setMaterialRows([{ ...emptyMaterialRow }]); }} className="p-2 hover:bg-red-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Type *">
                  <select value={consForm.material_type} onChange={e => setConsForm({...consForm, material_type: e.target.value})} className={selectCls}>
                    <option value="consumption">Consumption</option>
                    <option value="wastage">Wastage</option>
                  </select>
                </FormField>
                <FormField label="Date *">
                  <input type="date" value={consForm.entry_date} onChange={e => setConsForm({...consForm, entry_date: e.target.value})} className={inputCls} />
                </FormField>
              </div>
              <FormField label="Project *">
                <ProjectSelect projects={projects} value={consForm.project_id} onChange={v => setConsForm({...consForm, project_id: v})}
              placeholder="Select Project" className={selectCls} />
              </FormField>
              <FormField label="Department">
                <select value={consForm.department_id} onChange={e => setConsForm({...consForm, department_id: e.target.value})} className={selectCls}>
                  <option value="">Select Dept</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormField>
              <datalist id="mat-list">{MATERIAL_SUGGESTIONS.map(m => <option key={m} value={m} />)}</datalist>

              {/* Dynamic material rows */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">Materials * (multiple rows allowed)</label>
                  <button type="button"
                    onClick={() => setMaterialRows(r => [...r, { ...emptyMaterialRow }])}
                    className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1">
                    + Add Row
                  </button>
                </div>

                {/* Header */}
                <div className="grid grid-cols-12 gap-1 text-xs text-gray-400 font-medium px-1">
                  <div className="col-span-5">Material Name</div>
                  <div className="col-span-2">Qty</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-2">Rate</div>
                  <div className="col-span-1"></div>
                </div>

                {materialRows.map((row, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center bg-red-50/50 rounded-xl p-2 border border-red-100">
                    <div className="col-span-5">
                      <input
                        value={row.material_name}
                        onChange={e => setMaterialRows(rows => rows.map((r, idx) => idx===i ? {...r, material_name: e.target.value} : r))}
                        list="mat-list"
                        placeholder="e.g. Solid Wood"
                        className={inputCls + " text-xs py-1.5"}
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number" min="0" step="0.01"
                        value={row.consumed_qty}
                        onChange={e => setMaterialRows(rows => rows.map((r, idx) => idx===i ? {...r, consumed_qty: e.target.value} : r))}
                        placeholder="0"
                        className={inputCls + " text-xs py-1.5"}
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={row.unit}
                        onChange={e => setMaterialRows(rows => rows.map((r, idx) => idx===i ? {...r, unit: e.target.value} : r))}
                        className={selectCls + " text-xs py-1.5"}
                      >
                        {UNITS.map(u => <option key={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number" min="0" step="0.01"
                        value={row.rate_per_unit}
                        onChange={e => setMaterialRows(rows => rows.map((r, idx) => idx===i ? {...r, rate_per_unit: e.target.value} : r))}
                        placeholder="₹"
                        className={inputCls + " text-xs py-1.5"}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {materialRows.length > 1 && (
                        <button type="button"
                          onClick={() => setMaterialRows(rows => rows.filter((_, idx) => idx !== i))}
                          className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {/* Row total */}
                    {row.consumed_qty && row.rate_per_unit && (
                      <div className="col-span-12 text-right text-xs text-red-600 font-semibold pr-1">
                        = {formatCurrency((parseFloat(row.consumed_qty)||0) * (parseFloat(row.rate_per_unit)||0))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Grand Total */}
                {materialRows.some(r => r.consumed_qty && r.rate_per_unit) && (
                  <div className="bg-red-100 rounded-xl p-2 text-right text-sm font-bold text-red-700">
                    Total: {formatCurrency(materialRows.reduce((s, r) => s + (parseFloat(r.consumed_qty)||0) * (parseFloat(r.rate_per_unit)||0), 0))}
                  </div>
                )}
              </div>

              <FormField label="Notes"><textarea value={consForm.notes} onChange={e => setConsForm({...consForm, notes: e.target.value})} rows={2} className={inputCls} /></FormField>
              <div className="flex gap-3">
                <button onClick={handleConsSubmit} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold">Save Consumption</button>
                <button onClick={() => setShowConsForm(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
              </div>
            </div>
          </div>
            </div>
        </div>
      )}

      {/* Production Form Modal */}
      {showProdForm && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b bg-green-50">
              <h2 className="font-bold text-lg text-green-700">🏭 Add Production</h2>
              <button onClick={() => setShowProdForm(false)} className="p-2 hover:bg-green-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <FormField label="Date *">
                <input type="date" value={prodForm.entry_date} onChange={e => setProdForm({...prodForm, entry_date: e.target.value})} className={inputCls} />
              </FormField>
              <FormField label="Project *">
                <ProjectSelect projects={projects} value={prodForm.project_id} onChange={v => setProdForm({...prodForm, project_id: v})}
              placeholder="Select Project" className={selectCls} />
              </FormField>
              {prodItems.length > 0 && (
                <FormField label="Product/Item">
                  <select value={prodForm.project_item_id} onChange={e => setProdForm({...prodForm, project_item_id: e.target.value})} className={selectCls}>
                    <option value="">Select Item</option>
                    {prodItems.map(i => <option key={i.id} value={i.id}>{i.proto_code} — {i.item_name}</option>)}
                  </select>
                </FormField>
              )}
              <FormField label="Department">
                <select value={prodForm.department_id} onChange={e => setProdForm({...prodForm, department_id: e.target.value})} className={selectCls}>
                  <option value="">Select Dept</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormField>
              <FormField label="Item Name (Produced) *">
                <input value={prodForm.item_name} onChange={e => setProdForm({...prodForm, item_name: e.target.value})}
                  placeholder="e.g. SOFA, BED FRAME" className={inputCls} />
              </FormField>
              <div className="grid grid-cols-3 gap-2">
                <FormField label="Qty *"><input type="number" min="0" step="0.01" value={prodForm.produced_qty} onChange={e => setProdForm({...prodForm, produced_qty: e.target.value})} className={inputCls} /></FormField>
                <FormField label="Unit"><select value={prodForm.unit} onChange={e => setProdForm({...prodForm, unit: e.target.value})} className={selectCls}>{UNITS.map(u => <option key={u}>{u}</option>)}</select></FormField>
                <FormField label="Rate/Unit"><input type="number" min="0" step="0.01" value={prodForm.rate_per_unit} onChange={e => setProdForm({...prodForm, rate_per_unit: e.target.value})} className={inputCls} /></FormField>
              </div>
              {prodForm.produced_qty && prodForm.rate_per_unit && (
                <div className="bg-green-50 rounded-xl p-2 text-center text-sm font-bold text-green-700">
                  Amount: {formatCurrency((parseFloat(prodForm.produced_qty)||0) * (parseFloat(prodForm.rate_per_unit)||0))}
                </div>
              )}
              <FormField label="Notes"><textarea value={prodForm.notes} onChange={e => setProdForm({...prodForm, notes: e.target.value})} rows={2} className={inputCls} /></FormField>
              <div className="flex gap-3">
                <button onClick={handleProdSubmit} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl text-sm font-semibold">Save Production</button>
                <button onClick={() => setShowProdForm(false)} className="btn-secondary" style={{flex:1}}>Cancel</button>
              </div>
            </div>
          </div>
            </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
